/**
 * Exact fixed-point decimals for everything that is a number but not an
 * amount of money: FX rates, tax rates, allocation proportions, quantities.
 *
 * Backed by `bigint`; a Decimal's value is `units / 10^scale`. Floating point
 * does not appear in the money path (docs/22 §8).
 */
import { raise } from "../errors/index.js";
import { divideRounded } from "./rounding.js";
/** Монголбанк publishes to four places; eight leaves room for cross rates. IAS 21 is rate-sensitive. */
export const FX_RATE_SCALE = 8;
/** A fraction of a whole — a standalone-selling-price ratio, an expected-loss rate. */
export const PROPORTION_SCALE = 6;
const POW10 = [];
/** Ten to the `n`, memoised, and the one place a scale is validated. */
export function pow10(n) {
    if (n < 0 || !Number.isInteger(n)) {
        raise("VALUE_INVALID", { kind: "scale", value: String(n), reason: "not a non-negative integer" });
    }
    for (let i = POW10.length; i <= n; i++)
        POW10[i] = i === 0 ? 1n : POW10[i - 1] * 10n;
    return POW10[n];
}
export function decimal(units, scale) {
    pow10(scale);
    return { units, scale };
}
export const ZERO_DECIMAL = { units: 0n, scale: 0 };
export function decimalFromInt(value, scale = 0) {
    return decimal(BigInt(value) * pow10(scale), scale);
}
/**
 * Parse `"12.3456"`, `"-0.5"`, `"7"`. The scale comes from the literal —
 * `"1.50"` keeps two places. There is deliberately no scale parameter: to
 * change scale call {@link rescale} and name the rounding.
 */
export function parseDecimal(text) {
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text.trim());
    if (!match) {
        raise("VALUE_INVALID", { kind: "decimal", value: text, reason: "not a decimal literal" });
    }
    const [, sign, whole, fraction = ""] = match;
    return decimal(BigInt(`${sign}${whole}${fraction}`), fraction.length);
}
/** Change scale. Increasing is exact; decreasing rounds and must name a mode. */
export function rescale(value, scale, mode) {
    if (scale === value.scale)
        return value;
    if (scale > value.scale)
        return decimal(value.units * pow10(scale - value.scale), scale);
    return decimal(divideRounded(value.units, pow10(value.scale - scale), mode), scale);
}
function align(a, b) {
    const scale = Math.max(a.scale, b.scale);
    return { a: a.units * pow10(scale - a.scale), b: b.units * pow10(scale - b.scale), scale };
}
export function addDecimal(a, b) {
    const x = align(a, b);
    return decimal(x.a + x.b, x.scale);
}
export function subtractDecimal(a, b) {
    const x = align(a, b);
    return decimal(x.a - x.b, x.scale);
}
/** Exact — the result's scale is the sum of the operands'. */
export function multiplyDecimal(a, b) {
    return decimal(a.units * b.units, a.scale + b.scale);
}
/** Inexact by nature, so the target scale and mode are required. */
export function divideDecimal(a, b, scale, mode) {
    if (b.units === 0n)
        throw new RangeError("division by zero");
    const numerator = a.units * pow10(b.scale + scale);
    const denominator = b.units * pow10(a.scale);
    return decimal(divideRounded(numerator, denominator, mode), scale);
}
export function negateDecimal(value) {
    return decimal(-value.units, value.scale);
}
export function isZeroDecimal(value) {
    return value.units === 0n;
}
export function compareDecimal(a, b) {
    const x = align(a, b);
    return x.a === x.b ? 0 : x.a < x.b ? -1 : 1;
}
export function decimalToString(value) {
    if (value.scale === 0)
        return value.units.toString();
    const negative = value.units < 0n;
    const digits = (negative ? -value.units : value.units).toString().padStart(value.scale + 1, "0");
    const cut = digits.length - value.scale;
    return `${negative ? "-" : ""}${digits.slice(0, cut)}.${digits.slice(cut)}`;
}
//# sourceMappingURL=decimal.js.map